/**
 * PaymentService - Stripe integration and subscription management
 *
 * Boundary: Does not handle company/user CRUD - delegates to CompanyService/UserService
 * External dependencies:
 * - API: /api/createCheckoutSession, /api/verifyCheckoutSession, /api/createSupportTicket, /api/cancelSubscription
 * - Stripe: Client-side redirectToCheckout, loadStripe()
 * - Firebase Auth: ID tokens for API authentication
 */
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Observable, from, switchMap, BehaviorSubject, firstValueFrom, timeout } from 'rxjs';
import { Company } from '../models/company';
import { CompanyService } from './company.service';
import { CompanyStateService } from './company-state.service';
import { UserService } from './user.service';
import { LoggingService } from './logging.service';
import { ThemeService } from './theme.service';
import { TierAccessService, UserTier } from './tier-access.service';
import { UserActivityLoggingService } from './user-activity-logging.service';
import { Auth } from '@angular/fire/auth';
import { ApiConfigurationService } from './api-configuration.service';
import { getTrialEndDate } from '@core/utils/trial-dates';
import { readPendingPromoCode } from '@core/utils/pending-promo-code';
import { planActivatedMessage } from '@core/utils/plan-display-name';

export interface CheckoutSessionData {
  priceId?: string;
  planId: string;
  billingInterval?: 'monthly' | 'annual';
  successUrl?: string;
  cancelUrl?: string;
  customerEmail?: string;
  metadata?: { [key: string]: string };
  /**
   * Promotion code the customer entered on the plans page or brought on the
   * registration link (#943). The API resolves it against Stripe's active
   * promotion codes and attaches it to the session; the client never computes
   * what is charged. Omitted when there is no code.
   */
  couponCode?: string;
}

export interface SubscriptionResult {
  success: boolean;
  sessionId?: string;
  subscriptionId?: string;
  error?: string;
  redirectUrl?: string;
}

export interface SupportTicketData {
  currentCompanyId: string;
  userEmail: string;
  planRequested: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  category?:
    | 'upgrade_assistance'
    | 'billing_question'
    | 'technical_support'
    | 'sales_inquiry';
}

export interface UpgradeRequest {
  currentCompanyId: string;
  targetTier: UserTier;
  planId: string;
  priceId: string;
  billingInterval?: 'monthly' | 'annual';
  source: 'upgrade_prompt' | 'trial_expiration' | 'manual' | 'billing_page';
}

/**
 * Manages Stripe payment processing and subscription lifecycle for freemium model.
 *
 * Dependencies:
 * - HttpClient: API communication for payment operations
 * - CompanyService: Company tier updates post-payment
 * - UserService: User data retrieval for checkout
 * - TierAccessService: Tier validation and upgrades
 * - LoggingService: Error tracking and debug logs
 * - ThemeService: Toast notifications for user feedback
 * - UserActivityLoggingService: Analytics tracking for conversion funnel
 * - Auth (Firebase): ID tokens for secure API calls
 *
 * Side effects:
 * - BehaviorSubject: paymentProcessing$ (payment flow state)
 * - Navigation: window.location.href redirects to Stripe Checkout
 * - Subjects: Updates company tier in CompanyService
 * - Activity logging: Tracks upgrade attempts/completions via UserActivityLoggingService
 */
/**
 * True when a checkout would give the company its first plan: it has none
 * now (the stored no-plan value, or a lapsed trial) (#943).
 * @param company - The company checking out
 */
export function isFirstPlan(company: Pick<Company, 'tier'> | null | undefined): boolean {
  const tier = String(company?.tier ?? '');
  return tier === '' || tier === 'free' || tier === 'expired';
}

/**
 * The message an API error response carries (`{ error }`), falling back to a
 * caller-supplied sentence. Angular's own `HttpErrorResponse.message`
 * ("Http failure response for …: 500") tells the customer nothing (#876).
 * @param error - Whatever the HTTP call threw
 * @param fallback - Shown when the response carries no message
 * @returns A sentence fit for the UI
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const body = (error as { error?: unknown } | null)?.error;
  if (body && typeof body === 'object') {
    const message = (body as { error?: unknown; message?: unknown }).error ??
      (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) {
    try {
      const parsed = JSON.parse(body) as { error?: unknown };
      if (typeof parsed.error === 'string' && parsed.error.trim()) return parsed.error;
    } catch {
      // Not JSON: fall through to the fallback.
    }
  }
  return fallback;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private http = inject(HttpClient);
  private companyService = inject(CompanyService);
  private companyStateService = inject(CompanyStateService);
  private userService = inject(UserService);
  private tierAccessService = inject(TierAccessService);
  private loggingService = inject(LoggingService);
  private themeService = inject(ThemeService);
  private readonly activityLogging = inject(UserActivityLoggingService);
  private auth = inject(Auth);
  private platformId = inject(PLATFORM_ID);
  private apiConfigService = inject(ApiConfigurationService);

  private stripePromise: Promise<Stripe | null>;
  private readonly paymentProcessing = new BehaviorSubject<boolean>(false);
  public paymentProcessing$ = this.paymentProcessing.asObservable();

  constructor() {
    this.stripePromise = loadStripe(environment.stripe_api_key);
  }

  /**
   * Gets authentication headers with Firebase ID token and Azure Functions key.
   * Both are required: the Functions key satisfies Azure host authLevel:function,
   * and the Bearer token satisfies our Firebase-based business-logic auth.
   * @returns Promise of HTTP headers
   */
  private async getAuthHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    try {
      const currentUser = this.auth.currentUser;

      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers = headers.set('Authorization', `Bearer ${token}`);
      } else {
        throw new Error('No authenticated user found');
      }
    } catch (error) {
      this.loggingService.logError('Could not get Firebase auth token:', error);
      throw new Error('Authentication required');
    }

    // Include the Azure Functions host key so authLevel:function endpoints
    // accept the request before running function code.
    const { value: functionKey } = await this.apiConfigService.getApiFunctionToken();
    if (functionKey) {
      headers = headers.set('x-functions-key', functionKey);
    } else {
      throw new Error('API function token is empty — configuration may not have loaded with auth headers');
    }

    return headers;
  }

  /**
   * Creates Stripe checkout session and redirects user to payment flow.
   *
   * @param company - Company initiating the upgrade
   * @param planId - Plan identifier for tracking
   * @param priceId - Stripe price ID for the subscription
   * @returns SubscriptionResult with sessionId or error
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught and returned as { success: false, error: string }
   * - State touched: Updates paymentProcessing$, creates activity log, triggers Stripe redirect
   * - Idempotency: NOT idempotent; each call creates new Stripe session
   *
   * @example
   * const result = await paymentService.createCheckoutSession(company, 'premium-monthly', 'price_123');
   * if (result.success) {
   *   // User will be redirected to Stripe Checkout
   * }
   */
  async createCheckoutSession(
    company: Company,
    planId: string,
    priceId: string | undefined,
    billingInterval: 'monthly' | 'annual' = 'annual',
    couponCode?: string,
  ): Promise<SubscriptionResult> {
    this.loggingService.logInfo('PaymentService.createCheckoutSession: Entry', {
      currentCompanyId: company.uid,
      planId,
      priceId,
      billingInterval,
      couponCode: couponCode ?? null,
    });

    try {
      this.loggingService.logInfo('PaymentService.createCheckoutSession: Getting user from UserService');

      // Use firstValueFrom with timeout instead of deprecated toPromise()
      const user = await firstValueFrom(
        this.userService.getUserModel().pipe(
          timeout(10000) // 10 second timeout
        )
      );

      this.loggingService.logInfo('PaymentService.createCheckoutSession: User retrieved', {
        hasUser: !!user,
        userEmail: user?.email
      });

      if (!user) {
        this.loggingService.logError('PaymentService.createCheckoutSession: No user found');
        return {
          success: false,
          error: 'User not found',
        };
      }

      // SSR guard: window.location only available in browser
      if (!isPlatformBrowser(this.platformId)) {
        return {
          success: false,
          error: 'Payment checkout not available on server',
        };
      }

      // A code typed on the plans page wins; otherwise the one the customer
      // brought on the registration link (#943), if any.
      couponCode = couponCode || user.pendingPromoCode || readPendingPromoCode() || undefined;

      const checkoutData: CheckoutSessionData = {
        priceId,
        planId,
        billingInterval,
        // A company buying its first plan goes on to "Run your first scan"
        // after payment (#943); an upgrade returns to the dashboard.
        successUrl:
          `${window.location.origin}/register/checkout-success?session_id={CHECKOUT_SESSION_ID}` +
          (isFirstPlan(company) ? '&next=first-scan' : ''),
        cancelUrl: `${window.location.origin}/dashboard/company/subscription-plans`,
        customerEmail: user.email,
        metadata: {
          currentCompanyId: company.uid,
          userId: user.uid,
          planId: planId,
          tier: 'premium',
          ...(couponCode ? { couponCode } : {}),
        },
        ...(couponCode ? { couponCode } : {}),
      };

      this.loggingService.logInfo('PaymentService.createCheckoutSession: Calling API', {
        endpoint: `${environment.app_api_url}/api/createCheckoutSession`,
        priceId: checkoutData.priceId,
        customerEmail: checkoutData.customerEmail
      });

      // Get auth headers
      const headers = await this.getAuthHeaders();

      const response = await firstValueFrom(
        this.http
          .post<{
            sessionId: string;
            url: string;
            /** Present when a code was sent: false when Stripe would not take it. */
            promotionCodeApplied?: boolean;
          }>(`${environment.app_api_url}/api/createCheckoutSession`, checkoutData, { headers })
          .pipe(timeout(15000)) // 15 second timeout for API call
      );

      this.loggingService.logInfo('PaymentService.createCheckoutSession: API response received', {
        hasResponse: !!response,
        hasSessionId: !!response?.sessionId,
        sessionId: response?.sessionId
      });

      if (response?.promotionCodeApplied === false) {
        // Unknown, expired or not valid for this purchase. Checkout goes ahead
        // at the price Stripe shows, where the customer can still enter a
        // code; stop sending this one.
        this.loggingService.logWarning('Promotion code was not applied at checkout', {
          couponCode,
        });
        await this.userService.forgetPendingPromoCode(user);
      }

      if (response?.sessionId) {
        // Track upgrade attempt
        this.trackUpgradeAttempt(
          company,
          planId,
          'checkout_initiated',
          'checkout',
        );

        // Log plan upgrade initiation activity
        this.activityLogging.logUserAction('plan_upgrade_initiated', {
          currentTier: company.tier,
          targetTier: 'premium',
          planId,
          sessionId: response.sessionId,
          source: 'checkout_initiated',
        });

        // Redirect to Stripe Checkout using URL (redirectToCheckout deprecated Sept 2025)
        if (response.url) {
          window.location.href = response.url;
        } else {
          this.loggingService.logError('PaymentService: Checkout URL not returned from API', { sessionId: response.sessionId });
          return {
            success: false,
            error: 'Checkout session URL not available. Please contact support.',
          };
        }

        return {
          success: true,
          sessionId: response.sessionId,
        };
      }

      return {
        success: false,
        error: 'Failed to create checkout session',
      };
    } catch (error: any) {
      this.loggingService.handleError('Payment service error', error);
      return {
        success: false,
        error: apiErrorMessage(error, 'Checkout could not be started. Please try again or contact support.'),
      };
    }
  }

  /**
   * Verifies Stripe checkout session and upgrades company to premium tier.
   *
   * @param sessionId - Stripe checkout session ID from URL parameter
   * @returns Promise resolving to true if upgrade successful
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught, logged, and return false
   * - State touched: Updates company tier/subscription in CompanyService, creates activity log
   * - Idempotency: NOT idempotent; repeated calls will attempt to update company again
   *
   * @example
   * const success = await paymentService.handleSuccessfulPayment(sessionId);
   * if (success) {
   *   // Company upgraded to premium
   * }
   */
  async handleSuccessfulPayment(sessionId: string): Promise<boolean> {
    console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: ENTRY — sessionId =', sessionId);
    try {
      // Get auth headers
      console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: fetching auth headers...');
      const headers = await this.getAuthHeaders();
      console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: auth headers obtained, keys =', Object.keys(headers));

      // Verify session and get payment details from backend
      const apiUrl = `${environment.app_api_url}/api/verifyCheckoutSession`;
      console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: POSTing to', apiUrl, '{ sessionId:', sessionId, '}');
      const response = await firstValueFrom(
        this.http
          .post<{
            success: boolean;
            subscription: any;
            metadata: any;
            /** The plan state the API applied, or null (#945). */
            activation?: { tier: string; planId: string | null; subscriptionStatus: 'active' } | null;
          }>(apiUrl, { sessionId }, { headers })
          .pipe(timeout(10000))
      );

      console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: API response =', {
        success: response?.success,
        hasMetadata: !!response?.metadata,
        metadata: response?.metadata,
        subscriptionId: response?.subscription?.id,
      });

      if (response?.success && response.metadata) {
        const lookupCompanyId = response.metadata.currentCompanyId;
        console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: fetching company for id =', lookupCompanyId);
        // ✅ DO NOT WRITE TO FIRESTORE HERE - Stripe webhook is the authoritative source.
        // Optimistically patch the in-memory company tier so route guards see a paid tier
        // before the webhook fires. The webhook will overwrite Firestore with the real data.
        const company = await this.companyService.getCompany(
          lookupCompanyId,
        );
        console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: getCompany result =', {
          found: !!company,
          companyUid: company?.uid,
          currentTier: company?.tier,
          currentPlanId: company?.planId,
        });
        if (company) {
          const previousTier = company.tier;
          const newPlanId: string = response.metadata.planId || 'essentials';

          // The API has already applied the plan (#945): verifyCheckoutSession
          // runs the webhook's own company update, because the plan fields are
          // server-owned and the browser may not write them. Patch the
          // in-memory copies with what it applied so the guards see the plan
          // straight away; a re-read from Firestore returns the same values.
          const activation = response.activation;
          const appliedTier = (activation?.tier || newPlanId) as Company['tier'];
          const appliedPlanId = activation?.planId || newPlanId;
          for (const target of [this.companyService.company, company]) {
            if (target) {
              target.tier = appliedTier;
              target.planId = appliedPlanId;
              target.subscriptionStatus = 'active';
            }
          }
          if (this.companyStateService.currentState?.uid === company.uid) {
            this.companyStateService.setCurrentCompany(company);
          }

          // Log plan upgrade activity (analytics only, no state mutation)
          this.activityLogging.logUserAction('plan_upgraded', {
            previousTier,
            newTier: 'premium', // Expected tier after webhook processes
            planId: response.metadata.planId,
            subscriptionId: response.subscription.id,
            sessionId,
            upgradeSource: 'payment_success',
            note: 'Subscription state will be updated by Stripe webhook'
          });

          // Track successful conversion
          this.trackUpgradeAttempt(
            company,
            response.metadata.planId,
            'upgrade_completed',
            'payment_success',
          );

          // Name the plan they bought (#956): "Premium" is the internal tier,
          // not something a customer buys.
          this.themeService.triggerSuccess(planActivatedMessage(appliedPlanId));
          console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: returning TRUE — upgrade complete');
          return true;
        } else {
          console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: company NOT FOUND for id =', lookupCompanyId, '— returning false');
        }
      } else {
        console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: response.success is falsy or no metadata — returning false');
      }

      return false;
    } catch (error: any) {
      console.warn('[PAYMENT-DEBUG] handleSuccessfulPayment: ERROR caught =', {
        status: error?.status,
        statusText: error?.statusText,
        message: error?.message,
        error: error?.error,
      });
      this.loggingService.handleError('Payment verification error', error);
      this.themeService.triggerError(
        'Payment verification failed. Please contact support.',
      );
      return false;
    }
  }

  /**
   * Creates support ticket for complex upgrade scenarios (manual assistance).
   *
   * @param ticketData - Support ticket details including user info and upgrade request
   * @returns Promise resolving to true if ticket created successfully
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught, logged, and return false
   * - State touched: Creates support ticket via API, displays toast notification
   * - Idempotency: NOT idempotent; each call creates new support ticket
   */
  async createUpgradeTicket(ticketData: SupportTicketData): Promise<boolean> {
    try {
      // Get auth headers
      const headers = await this.getAuthHeaders();

      const response = await this.http
        .post<{ success: boolean; ticketId: string }>(
          `${environment.app_api_url}/api/createSupportTicket`,
          {
            type: 'subscription_upgrade',
            ...ticketData,
            subject: `Subscription Upgrade Request - ${ticketData.planRequested}`,
            timestamp: new Date().toISOString(),
          },
          { headers }
        )
        .toPromise();

      if (response?.success) {
        this.themeService.triggerSuccess(
          `Support ticket created successfully! Ticket ID: ${response.ticketId}. Our team will contact you within 24 hours.`,
        );
        return true;
      }

      return false;
    } catch (error: any) {
      this.loggingService.handleError('Support ticket creation error', error);
      this.themeService.triggerError(
        'Failed to create support ticket. Please try again or contact support directly.',
      );
      return false;
    }
  }

  /**
   * Detects and processes successful payment redirect from Stripe.
   *
   * @returns Promise<boolean> true when the payment verification succeeds
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Handled by handleSuccessfulPayment
   * - State touched: Cleans up URL query params, triggers payment verification
   * - Idempotency: Safe to call multiple times; only acts if session_id present
   */
  async checkForPaymentSuccess(): Promise<boolean> {
    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: START');
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: not in browser context, returning false');
      return false;
    }

    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: window.location.href =', window.location.href);
    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: window.location.search =', window.location.search);
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: session_id extracted =', sessionId);

    if (!sessionId) {
      console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: no session_id in URL, returning false');
      return false;
    }

    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: calling handleSuccessfulPayment with sessionId =', sessionId);
    const success = await this.handleSuccessfulPayment(sessionId);
    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: handleSuccessfulPayment returned', success);
    window.history.replaceState({}, document.title, window.location.pathname);
    console.warn('[PAYMENT-DEBUG] checkForPaymentSuccess: URL cleaned up, returning', success);
    return success;
  }

  /**
   * Fetches available pricing plans from API.
   *
   * @returns Observable emitting pricing data by plan ID
   * - Observable semantics: Single emit with pricing data; completes after response
   * - HTTP errors: Not handled - propagates to caller
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  getPlanPricing(): Observable<{
    [planId: string]: { priceId: string; amount: number };
  }> {
    return this.http.get<{
      [planId: string]: { priceId: string; amount: number };
    }>(`${environment.app_api_url}/api/getPlanPricing`);
  }

  /**
   * Track upgrade attempts for analytics
   */
  private trackUpgradeAttempt(
    company: Company,
    planId: string,
    event: string,
    source?: string,
  ): void {
    const analyticsEvent = {
      event,
      properties: {
        currentCompanyId: company.uid,
        planId,
        currentTier: company.tier,
        source: source || 'unknown',
        timestamp: new Date().toISOString(),
        trialDaysUsed: this.calculateTrialDaysUsed(company),
      },
    };

    this.loggingService.logInfo(`Upgrade tracking: ${event}`, analyticsEvent);

    // TODO: Integrate with actual analytics service (e.g., Mixpanel, Google Analytics, etc.)
    // this.analyticsService.track(event, analyticsEvent.properties);
  }

  /**
   * Retrieves current subscription status from Stripe.
   *
   * @param subscriptionId - Stripe subscription ID
   * @returns Promise with subscription active status and renewal date
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught and return { active: false, status: 'error' }
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  async checkSubscriptionStatus(subscriptionId: string): Promise<{
    active: boolean;
    status: string;
    currentPeriodEnd?: Date;
  }> {
    try {
      const response = await this.http
        .get<{
          active: boolean;
          status: string;
          currentPeriodEnd?: string;
        }>(`/api/subscription-status/${subscriptionId}`)
        .toPromise();

      return {
        active: response?.active || false,
        status: response?.status || 'unknown',
        currentPeriodEnd: response?.currentPeriodEnd
          ? new Date(response.currentPeriodEnd)
          : undefined,
      };
    } catch (error: any) {
      this.loggingService.handleError('Subscription status check error', error);
      return {
        active: false,
        status: 'error',
      };
    }
  }

  /**
   * Cancels active Stripe subscription.
   *
   * @param subscriptionId - Stripe subscription ID to cancel
   * @returns Promise resolving to true if cancellation successful
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught, logged, and return false
   * - State touched: Displays toast notification on success/failure
   * - Idempotency: Calling on already-canceled subscription returns false
   */
  async cancelSubscription(subscriptionId: string): Promise<boolean> {
    try {
      // Get auth headers
      const headers = await this.getAuthHeaders();

      const response = await this.http
        .post<{
          success: boolean;
        }>(`${environment.app_api_url}/api/cancelSubscription`, { subscriptionId }, { headers })
        .toPromise();

      if (response?.success) {
        this.themeService.triggerSuccess(
          'Subscription cancelled successfully.',
        );
        return true;
      }

      return false;
    } catch (error: any) {
      this.loggingService.handleError('Subscription cancellation error', error);
      this.themeService.triggerError(
        'Failed to cancel subscription. Please contact support.',
      );
      return false;
    }
  }

  /**
   * Orchestrates complete trial-to-premium upgrade flow with validation.
   *
   * @param upgradeRequest - Upgrade details including company, tier, and source
   * @returns Promise with SubscriptionResult (success or error details)
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught and returned as { success: false, error: string }
   * - State touched: Updates paymentProcessing$ and creates a checkout session; never writes the tier (#945)
   * - Idempotency: NOT idempotent; each call initiates new upgrade attempt
   */
  async processUpgrade(
    upgradeRequest: UpgradeRequest,
  ): Promise<SubscriptionResult> {
    try {
      this.paymentProcessing.next(true);
      this.loggingService.logInfo('Processing tier upgrade', upgradeRequest);

      // Get company data
      const company = await this.companyService.getCompany(
        upgradeRequest.currentCompanyId,
      );
      if (!company) {
        throw new Error('Company not found');
      }

      // Validate upgrade request
      const currentTier = company.tier as UserTier;
      if (currentTier === upgradeRequest.targetTier) {
        throw new Error('Already on target tier');
      }

      // For premium upgrades, create checkout session
      if (upgradeRequest.targetTier === UserTier.PREMIUM) {
        const result = await this.createCheckoutSession(
          company,
          upgradeRequest.planId,
          upgradeRequest.priceId,
          upgradeRequest.billingInterval || 'annual',
        );

        if (result.success) {
          // Track upgrade attempt
          this.trackUpgradeAttempt(
            company,
            upgradeRequest.planId,
            'upgrade_initiated',
            upgradeRequest.source,
          );
        }

        return result;
      } else {
        // Plan and trial fields are server-owned (#945): the only self-serve
        // change is a paid checkout. Trials and other changes go through sales.
        return {
          success: false,
          error: 'Plan changes other than a paid upgrade are made by our team. Please contact us.',
        };
      }
    } catch (error: any) {
      this.loggingService.handleError('Upgrade processing failed', error);
      this.themeService.triggerError(
        'Upgrade failed. Please try again or contact support.',
      );
      return { success: false, error: error.message };
    } finally {
      this.paymentProcessing.next(false);
    }
  }

  /**
   * Creates support ticket for users needing upgrade assistance.
   *
   * @param currentCompanyId - Company ID requesting assistance
   * @param userEmail - User's email for follow-up
   * @param currentTier - Current subscription tier
   * @returns Promise resolving to true if ticket created
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Handled by createUpgradeTicket
   * - State touched: Creates support ticket, displays notification
   * - Idempotency: NOT idempotent; creates new ticket each time
   */
  async requestUpgradeAssistance(
    currentCompanyId: string,
    userEmail: string,
    currentTier: string,
  ): Promise<boolean> {
    const ticketData: SupportTicketData = {
      currentCompanyId,
      userEmail,
      planRequested: 'Paid plan',
      message: `User is requesting assistance with upgrading their account. Current tier: ${currentTier}. Please contact them to discuss upgrade options and provide personalized assistance.`,
      priority: 'medium',
      category: 'upgrade_assistance',
    };

    return this.createUpgradeTicket(ticketData);
  }

  /**
   * Creates sales inquiry ticket for custom/enterprise plans.
   *
   * @param currentCompanyId - Company ID making inquiry
   * @param userEmail - User's email for follow-up
   * @param inquiryType - Type of inquiry (e.g., "Enterprise", "Custom")
   * @param message - Detailed inquiry message
   * @returns Promise resolving to true if inquiry submitted
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Handled by createUpgradeTicket
   * - State touched: Creates support ticket
   * - Idempotency: NOT idempotent; creates new inquiry each time
   */
  async requestSalesContact(
    currentCompanyId: string,
    userEmail: string,
    inquiryType: string,
    message: string,
  ): Promise<boolean> {
    const ticketData: SupportTicketData = {
      currentCompanyId,
      userEmail,
      planRequested: inquiryType,
      message: `Sales inquiry: ${inquiryType}\n\nMessage: ${message}`,
      priority: 'medium',
      category: 'sales_inquiry',
    };

    return this.createUpgradeTicket(ticketData);
  }

  /**
   * Validates whether user can upgrade based on current tier.
   *
   * @param _userId - User ID (currently unused - uses UserService current user)
   * @returns Promise with upgrade eligibility and optional reason/company
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught and return { canUpgrade: false, reason: 'Validation failed' }
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  async canUpgrade(
    _userId: string,
  ): Promise<{ canUpgrade: boolean; reason?: string; company?: Company }> {
    try {
      const user = await this.userService
        .getUserModel()
        .pipe(switchMap((user) => from(Promise.resolve(user))))
        .toPromise();

      if (!user || !user.currentCompanyId) {
        return { canUpgrade: false, reason: 'User or company not found' };
      }

      const company = await this.companyService.getCompany(
        user.currentCompanyId,
      );
      if (!company) {
        return { canUpgrade: false, reason: 'Company not found' };
      }

      if (company.tier === 'premium') {
        return {
          canUpgrade: false,
          reason: 'Already on premium tier',
          company,
        };
      }

      return { canUpgrade: true, company };
    } catch (error: any) {
      this.loggingService.handleError('Upgrade validation failed', error);
      return { canUpgrade: false, reason: 'Validation failed' };
    }
  }

  /**
   * Retrieves billing summary for company.
   *
   * @param currentCompanyId - Company ID to fetch billing for
   * @returns Promise with billing details or null if unavailable
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught, logged, and return null
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   *
   * TODO: Clarify API endpoint - currently points to non-existent /api/billing-summary
   */
  async getBillingSummary(currentCompanyId: string): Promise<{
    currentPlan: string;
    nextBillingDate?: string;
    amount?: number;
    status: string;
  } | null> {
    try {
      const response = await this.http
        .get<{
          currentPlan: string;
          nextBillingDate?: string;
          amount?: number;
          status: string;
        }>(`/api/billing-summary/${currentCompanyId}`)
        .toPromise();

      return response || null;
    } catch (error: any) {
      this.loggingService.handleError(
        'Billing summary retrieval failed',
        error,
      );
      return null;
    }
  }

  /**
   * Calculate how many days of trial were used
   */
  private calculateTrialDaysUsed(company: Company): number {
    if (!company.trialStartDate) return 0;

    const trialStart = new Date(company.trialStartDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - trialStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.min(diffDays, 30); // Max 30 days for trial
  }

  /**
   * Calculates remaining days in trial period.
   *
   * @param company - Company with trial dates; `trialEndDate` wins (see getTrialEndDate)
   * @returns Object with days remaining and active status
   * - Observable semantics: N/A (pure calculation)
   * - HTTP errors: N/A (no API calls)
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  getTrialTimeRemaining(company: Company): { days: number; isActive: boolean } {
    const trialEnd = company?.tier === 'trial' ? getTrialEndDate(company) : null;
    if (!trialEnd) {
      return { days: 0, isActive: false };
    }

    const now = new Date();

    if (now >= trialEnd) {
      return { days: 0, isActive: false };
    }

    const remainingDays = Math.ceil(
      (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    return { days: remainingDays, isActive: true };
  }

  /**
   * Creates or retrieves Stripe customer record for user during signup/login.
   *
   * @returns Promise with customer ID and creation status
   * - Observable semantics: N/A (async/await pattern)
   * - HTTP errors: Caught and return { success: false, error: string }
   * - State touched: Creates Stripe customer if not exists
   * - Idempotency: Safe to call multiple times; returns existing customer if found
   *
   * @example
   * const result = await paymentService.createOrGetStripeCustomer();
   * if (result.success) {
   *   console.log(`Customer ${result.customerId} ${result.isNew ? 'created' : 'retrieved'}`);
   * }
   */
  async createOrGetStripeCustomer(): Promise<{
    success: boolean;
    customerId?: string;
    isNew?: boolean;
    error?: string;
  }> {
    try {
      // Get auth headers
      const headers = await this.getAuthHeaders();

      const response = await this.http
        .post<{
          success: boolean;
          customerId: string;
          isNew: boolean;
        }>(`${environment.app_api_url}/api/stripe/customer`, {}, { headers })
        .toPromise();

      if (response && response.success) {
        this.loggingService.logInfo(
          `Stripe customer ${response.isNew ? 'created' : 'retrieved'}: ${response.customerId}`,
        );
        return {
          success: true,
          customerId: response.customerId,
          isNew: response.isNew,
        };
      }

      return {
        success: false,
        error: 'Failed to create/retrieve Stripe customer',
      };
    } catch (error: any) {
      // Don't fail the login flow if Stripe customer creation fails
      // Just log the error and continue
      console.warn('Failed to create/retrieve Stripe customer:', error);
      this.loggingService.handleError(
        'Stripe customer creation failed',
        error,
      );
      return {
        success: false,
        error: error?.error?.error || error?.message || 'Unknown error',
      };
    }
  }

  /**
   * Creates Stripe Customer Portal session for self-service billing management.
   *
   * @param currentCompanyId - Company ID for portal access
   * @param returnUrl - URL to return after portal session
   * @returns Observable emitting portal session URL
   * - Observable semantics: Single emit with URL; completes after response
   * - HTTP errors: Not handled - propagates to caller
   * - State touched: None (generates one-time portal URL)
   * - Idempotency: Each call generates new portal session
   */
  createCustomerPortalSession(
    currentCompanyId: string,
    returnUrl: string,
  ): Observable<{ url: string }> {
    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<{ url: string }>(
          `${environment.app_api_url}/api/createCustomerPortalSession`,
          {
            currentCompanyId,
            returnUrl,
          },
          { headers }
        )
      )
    );
  }

  /**
   * Retrieves payment method details for company.
   *
   * @param currentCompanyId - Company ID to fetch payment method for
   * @returns Observable emitting payment method details (card info)
   * - Observable semantics: Single emit; completes after response
   * - HTTP errors: Not handled - propagates to caller
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   *
   * TODO: Clarify API endpoint - currently points to non-existent /api/payment-method
   */
  getPaymentMethod(
    currentCompanyId: string,
  ): Observable<{ card?: { last4: string; brand: string; expMonth: number; expYear: number } }> {
    return this.http.get<{
      card?: { last4: string; brand: string; expMonth: number; expYear: number };
    }>(`/api/payment-method/${currentCompanyId}`);
  }

  /**
   * Retrieves billing history (invoices) for company.
   *
   * @param currentCompanyId - Company ID to fetch invoices for
   * @param limit - Maximum number of invoices to return (default: 10)
   * @returns Observable emitting invoice array
   * - Observable semantics: Single emit with invoice list; completes after response
   * - HTTP errors: Not handled - propagates to caller
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   *
   * TODO: Clarify API endpoint - currently points to non-existent /api/billing-history
   */
  getBillingHistory(
    currentCompanyId: string,
    limit: number = 10,
  ): Observable<{
    invoices: Array<{
      id: string;
      date: Date;
      description: string;
      amount: number;
      status: 'paid' | 'pending' | 'failed';
      invoiceUrl?: string;
    }>;
  }> {
    return this.http.get<{
      invoices: Array<{
        id: string;
        date: Date;
        description: string;
        amount: number;
        status: 'paid' | 'pending' | 'failed';
        invoiceUrl?: string;
      }>;
    }>(`/api/billing-history/${currentCompanyId}?limit=${limit}`);
  }
}

